<template>
    <v-container class="py-1">
        <v-row align="start" dense>
            <v-col cols="12" sm="8" class="py-0 px-2">
                <v-container>
                    <v-row
                        justify="center"
                        align="center"
                        class="editor-screen grey lighten-4"
                    >
                        <img
                            v-if="imageToEdit.file"
                            class="image-editing"
                            ref="source"
                            :src="objectUrl"
                        />
                        <v-icon v-else size="300">mdi-image-edit</v-icon>
                    </v-row>
                    <v-row
                        v-show="imageToEdit.file"
                        justify="center"
                        align="center"
                        class="pt-0 pt-md-2"
                    >
                        <v-btn-toggle v-model="btn1" class="mr-0 mr-md-1">
                            <v-tooltip v-for="(item, index) in btnItems1" :key="index" bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        :class="item.color"
                                        small
                                        @click.stop="item.exe"
                                        v-on="on"
                                    >
                                        <v-icon small>{{ item.icon }}</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ item.text }}</span>
                            </v-tooltip>
                        </v-btn-toggle>
                        <v-btn-toggle v-model="btn2" class="ml-0 ml-md-1">
                            <v-tooltip v-for="(item, index) in btnItems2" :key="index" bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        :class="item.color"
                                        small
                                        @click.stop="item.exe"
                                        v-on="on"
                                    >
                                        <v-icon small>{{ item.icon }}</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ item.text }}</span>
                            </v-tooltip>
                        </v-btn-toggle>
                    </v-row>
                </v-container>
            </v-col>
            <v-col class="pa-0 grey lighten-4" cols="12" sm="4">
                <v-toolbar dark dense class="grey darken-3 elevation-0">
                    <v-toolbar-title class="title">Vista previa</v-toolbar-title>
                </v-toolbar>
                <v-img class="image-preview mx-auto" contain :src="previewCropped" />
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
/**
 * Editor Package Library
 * @author José Luis Ruiz <jose.ruiz@jbge.com.mx>
 */

import Cropper from 'cropperjs'
import debounce from 'lodash/debounce'

export default {
  props: {
    imageToEdit: {
      type: Object,
      required: true,
      default: () => {}
    }
  },
  data() {
    return {
      cropper: null,
      btn1: null,
      btn2: null,
      objectUrl: null,
      previewCropped: null,
      debouncedUpdatePreview: debounce(this.updatePreview, 216),
      btnItems1: [
        {
          text: 'Reiniciar',
          icon: 'mdi-aspect-ratio',
          exe: () => this.actions('reset-cropper')
        },
        {
          text: 'Mover',
          icon: 'mdi-arrow-all',
          exe: () => this.actions('move')
        },
        {
          text: 'Recortar',
          icon: 'mdi-crop',
          exe: () => this.actions('crop')
        },
        {
          text: 'Acercar',
          icon: 'mdi-magnify-plus',
          exe: () => this.actions('zoom-in')
        },
        {
          text: 'Alejar',
          icon: 'mdi-magnify-minus',
          exe: () => this.actions('zoom-out')
        },
        {
          text: 'Espejo horizontal',
          icon: 'mdi-flip-horizontal',
          exe: () => this.actions('mirror-horizontal')
        },
        {
          text: 'Espejo vertical',
          icon: 'mdi-flip-vertical',
          exe: () => this.actions('mirror-vertical')
        }
      ],
      btnItems2: [
        {
          text: 'Mover arriba',
          icon: 'mdi-arrow-up-bold',
          exe: () => this.actions('move-up')
        },
        {
          text: 'Mover abajo',
          icon: 'mdi-arrow-down-bold',
          exe: () => this.actions('move-down')
        },
        {
          text: 'Mover derecha',
          icon: 'mdi-arrow-right-bold',
          exe: () => this.actions('move-right')
        },
        {
          text: 'Mover izquierda',
          icon: 'mdi-arrow-left-bold',
          exe: () => this.actions('move-left')
        },
        {
          text: 'Rotar izquierda',
          icon: 'mdi-rotate-left',
          exe: () => this.actions('rotate-left')
        },
        {
          text: 'Rotar derecha',
          icon: 'mdi-rotate-right',
          exe: () => this.actions('rotate-right')
        },
        {
          text: 'Guardar',
          icon: 'mdi-content-save',
          color: 'green',
          exe: () => this.saveImage()
        }
      ]
    }
  },
  methods: {
    actions(action) {
      switch (action) {
        case 'reset-cropper':
          this.cropper.clear()
          this.cropper.reset()
          break
        case 'move':
          this.cropper.setDragMode('move')
          break
        case 'crop':
          this.cropper.setDragMode('crop')
          break
        case 'zoom-in':
          this.cropper.zoom(0.1)
          break
        case 'zoom-out':
          this.cropper.zoom(-0.1)
          break
        case 'rotate-left':
          this.cropper.rotate(-45)
          break
        case 'rotate-right':
          this.cropper.rotate(45)
          break
        case 'move-right':
          this.cropper.move(2, 0)
          break
        case 'move-left':
          this.cropper.move(-2, 0)
          break
        case 'move-up':
          this.cropper.move(0, -2)
          break
        case 'move-down':
          this.cropper.move(0, 2)
          break
        case 'mirror-horizontal':
          this.cropper.scaleX(-this.cropper.getData().scaleX || -1)
          break
        case 'mirror-vertical':
          this.cropper.scaleY(-this.cropper.getData().scaleY || -1)
          break
      }
    },
    setupCropper(selectedFile) {
      if (this.cropper) {
        this.cropper.destroy()
      }

      if (this.objectUrl) {
        window.URL.revokeObjectURL(this.objectUrl)
      }

      if (!selectedFile) {
        this.cropper = null
        this.objectUrl = null
        this.previewCropped = null
      } else {
        this.objectUrl = window.URL.createObjectURL(selectedFile)
        this.$nextTick(this.setupCropperInstance)
      }
    },
    setupCropperInstance() {
      this.cropper = new Cropper(this.$refs.source, {
        autoCrop: false,
        dragMode: 'move',
        background: false,
        crop: this.debouncedUpdatePreview
        // crop: this.updatePreview  Do not use. It will slow down edition!!!🐐
      })
    },
    updatePreview() {
      const canvas = this.cropper.getCroppedCanvas()
      this.previewCropped = canvas.toDataURL('image/png')
    },
    saveImage() {
      this.btn1 = null
      this.btn2 = null
      this.$emit('saveImage', this.previewCropped)
    }
  },
  watch: {
    imageToEdit: {
      deep: true,
      handler(newVal) {
        if (newVal) {
          this.previewCropped = newVal.src
          this.setupCropper(newVal.originalFile)
        }
      }
    }
  },
  mounted() {
    const { imageToEdit } = this
    if (imageToEdit) {
      this.previewCropped = imageToEdit.src
      this.setupCropper(imageToEdit.originalFile)
    }
  }
}
</script>

<style lang="css" scoped>
.image-preview {
  display: block;
  max-width: 100%;
  max-height: 380px;
}
.editor-screen {
  height: 75vh;
}
.image-editing {
  display: block;
}
.image {
  height: 100%;
  width: 100%;
}
</style>
